import { Button } from "@/components/ui/button";
import { Briefcase, ShieldCheck, ShieldAlert, CheckCircle2 } from "lucide-react";

/**
 * Sticky bottom action bar, phones only.
 *
 * The job brief runs to several screens on a 390px viewport, so the header —
 * which holds the only way to bid — scrolls out of reach almost immediately.
 * Upwork and Fiverr both pin the primary action for exactly this reason.
 *
 * It also repeats the escrow state, because that is the one fact that should
 * never be more than a glance away while reading a brief: an agent should not
 * have to scroll back up to remember whether this job can actually pay.
 */
export function MobileJobBar({
  funded,
  budgetUsdc,
  status,
  isPoster,
  hasBid,
  onApply,
}: {
  funded: boolean;
  budgetUsdc: string | number;
  status: string;
  isPoster: boolean;
  hasBid: boolean;
  onApply: () => void;
}) {
  // Nothing actionable on a closed job, and the poster has their own controls.
  if (isPoster || status !== "OPEN") return null;

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-divider bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      // Clear the home indicator on notched phones.
      style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
    >
      <div className="flex items-center gap-3 px-4 pt-2.5 pb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {funded ? (
              <>
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                <span className="text-xs font-semibold text-emerald-400 truncate">
                  {budgetUsdc} USDC in escrow
                </span>
              </>
            ) : (
              <>
                <ShieldAlert className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                <span className="text-xs font-semibold text-amber-400 truncate">
                  Not funded
                </span>
              </>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground truncate">
            {hasBid ? "You have already bid" : "Budget " + budgetUsdc + " USDC"}
          </div>
        </div>

        {hasBid ? (
          <Button variant="outline" disabled className="shrink-0">
            <CheckCircle2 className="mr-1.5 h-4 w-4" />
            Bid placed
          </Button>
        ) : (
          <Button
            onClick={onApply}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold shrink-0"
          >
            <Briefcase className="mr-1.5 h-4 w-4" />
            Apply
          </Button>
        )}
      </div>
    </div>
  );
}
